import React from "react";
import { Shield, Eye, Lock, Database, Mail, Phone } from "lucide-react";
import Card from "../modern-components/ui/Card";

const sections = [
  {
    title: "1. Information We Collect",
    content: [
      "Personal identification information (name, email address, phone number, company name)",
      "Business details required for B2B transactions (GSTIN, company registration details)",
      "Order and transaction history",
      "Communication records and support queries",
      "Device and usage data (IP address, browser type, pages visited) for analytics",
    ],
  },
  {
    title: "2. How We Use Your Information",
    content: [
      "To process and fulfill your orders and quote requests",
      "To communicate order status, delivery updates, and support",
      "To send promotional offers, newsletters, and product updates (with your consent)",
      "To improve our platform, products, and services",
      "To comply with legal obligations and resolve disputes",
    ],
  },
  {
    title: "3. Sharing of Information",
    content: [
      "We do not sell or rent your personal data to third parties.",
      "We may share data with trusted logistics partners solely to fulfill your orders.",
      "Payment processors receive only the data necessary to complete transactions securely.",
      "We may disclose information if required by law or to protect the rights of lohamart.",
    ],
  },
  {
    title: "4. Data Security",
    content: [
      "All data is transmitted over SSL/TLS encrypted connections.",
      "Access to personal data is restricted to authorized personnel only.",
      "We conduct regular security assessments to protect your information.",
      "In the event of a data breach, we will notify affected users promptly.",
    ],
  },
  {
    title: "5. Cookies & Tracking",
    content: [
      "We use cookies to remember your preferences and maintain your session.",
      "Analytics cookies help us understand how users interact with our platform.",
      "You can disable cookies via your browser settings; some features may be affected.",
    ],
  },
  {
    title: "6. Your Rights",
    content: [
      "You have the right to access, correct, or delete your personal data.",
      "You may opt out of marketing communications at any time.",
      "To exercise your rights, contact us at privacy@lohamart.com.",
      "We will respond to requests within 30 working days.",
    ],
  },
  {
    title: "7. Data Retention",
    content: [
      "We retain your data for as long as your account is active or as required by law.",
      "Transaction records are kept for a minimum of 7 years for regulatory compliance.",
      "You may request deletion of your account and associated data at any time.",
    ],
  },
  {
    title: "8. Changes to This Policy",
    content: [
      "We may update this Privacy Policy periodically. The latest version will always be available on this page.",
      "Continued use of the platform after changes constitutes acceptance of the updated policy.",
    ],
  },
];

const highlights = [
  { icon: Shield, label: "Data Protected", desc: "SSL encrypted & secure" },
  { icon: Eye, label: "Transparent", desc: "Clear data practices" },
  {
    icon: Lock,
    label: "No Third-Party Sales",
    desc: "Your data stays private",
  },
  { icon: Database, label: "Minimal Collection", desc: "Only what we need" },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-40 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full border border-white/20 mb-6">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">LEGAL</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-100">
            How lohamart collects, uses, and protects your information.
          </p>
          <p className="text-sm text-gray-300 mt-3">
            Last updated: February 2025
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {highlights.map(({ icon: Icon, label, desc }) => (
            <Card key={label} className="text-center border border-gray-100">
              <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon className="w-6 h-6 text-primary-600" />
              </div>
              <div className="font-semibold text-gray-900 text-sm">{label}</div>
              <div className="text-xs text-gray-500 mt-1">{desc}</div>
            </Card>
          ))}
        </div>

        {/* Intro */}
        <Card className="mb-8 bg-blue-50 border border-blue-100">
          <p className="text-gray-700 leading-relaxed">
            At <strong>lohamart India Private Limited</strong>, we are committed
            to protecting your privacy. This Privacy Policy explains what
            information we collect when you use our platform, how we use it, and
            your rights regarding that information. By using lohamart, you agree
            to the terms described here.
          </p>
        </Card>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <Card key={section.title} className="border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-3">
                {section.title}
              </h2>
              <ul className="space-y-2">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-2" />
                    <span className="text-gray-700 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="mt-8 bg-gradient-to-br from-primary-600 to-secondary-600 text-white border-0">
          <h2 className="text-xl font-bold mb-4">Contact Us About Privacy</h2>
          <p className="text-gray-200 mb-4">
            If you have questions or concerns about this Privacy Policy, reach
            out to us:
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-yellow-300" />
              <span>info@lohamart.in</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-5 h-5 text-yellow-300" />
              <span>+91 94370 45109</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
